import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LgpdService } from '../../../core/services/lgpd.service';
import { LocaleService } from '../../../core/services/locale.service';
import { TranslocoService } from '@jsverse/transloco';
import { TurnstileWidgetComponent } from '../../../shared/turnstile-widget/turnstile-widget.component';

export const LGPD_DIREITOS: Array<{ value: string; label: string }> = [
  { value: 'eliminacao_consentimento', label: 'Eliminação dos dados pessoais tratados com o consentimento (art. 18, VI)' },
  { value: 'confirmacao_existencia', label: 'Confirmação da existência de tratamento' },
  { value: 'acesso', label: 'Acesso aos dados pessoais' },
  { value: 'correcao', label: 'Correção de dados incompletos, inexatos ou desatualizados' },
  { value: 'anonimizacao_bloqueio_eliminacao', label: 'Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou em desconformidade' },
  { value: 'portabilidade', label: 'Portabilidade dos dados a outro fornecedor' },
  { value: 'info_compartilhamento', label: 'Informação sobre entidades com as quais houve uso compartilhado de dados' },
  { value: 'info_consentimento', label: 'Informação sobre a possibilidade de não fornecer consentimento e consequências' },
  { value: 'revogacao_consentimento', label: 'Revogação do consentimento' },
];

@Component({
  selector: 'app-excluir-dados',
  templateUrl: './excluir-dados.component.html',
  styleUrls: ['./excluir-dados.component.scss'],
  standalone: false,
})
export class ExcluirDadosComponent implements OnInit {
  breadCrumbItems: Array<{ label: string; link?: string; active?: boolean }> = [];
  form!: UntypedFormGroup;
  submitted = false;
  enviando = false;
  enviadoMsg: string | null = null;
  erroMsg: string | null = null;
  turnstileToken = '';
  readonly direitos = LGPD_DIREITOS;

  @ViewChild(TurnstileWidgetComponent) turnstile?: TurnstileWidgetComponent;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly lgpd: LgpdService,
    private readonly transloco: TranslocoService,
    public readonly locale: LocaleService,
  ) {}

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'common.home', link: '/' },
      { label: 'lgpd.crumb', active: true },
    ];

    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(120)]],
      cpf: ['', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)]],
      email: ['', [Validators.required, Validators.email]],
      telefone: [''],
      direito: [this.direitos[0].value, [Validators.required]],
      identificador: [''],
      descricao: ['', [Validators.required, Validators.maxLength(5000)]],
      tem_representante: [false],
      representante_nome: [''],
      representante_cpf: [''],
      representante_vinculo: [''],
      declaracao_titular: [false, [Validators.requiredTrue]],
      ciencia_art16: [false, [Validators.requiredTrue]],
    });

    this.form.get('tem_representante')?.valueChanges.subscribe((on) => {
      this.aplicarValidacaoRepresentante(!!on);
    });

    if (document.documentElement.scrollTop > 40) {
      document.querySelector('.navbar')?.classList.add('navbar-stuck');
    }
  }

  get f() {
    return this.form.controls;
  }

  maskCpf(controlName: 'cpf' | 'representante_cpf'): void {
    const control = this.form.get(controlName);
    if (!control) {
      return;
    }
    const digits = String(control.value ?? '').replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 9) {
      masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    } else if (digits.length > 6) {
      masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    } else if (digits.length > 3) {
      masked = `${digits.slice(0, 3)}.${digits.slice(3)}`;
    }
    control.setValue(masked, { emitEvent: false });
  }

  maskTelefone(): void {
    const control = this.form.get('telefone');
    if (!control) {
      return;
    }
    const digits = String(control.value ?? '').replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 6) {
      masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    } else if (digits.length > 2) {
      masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    } else if (digits.length > 0) {
      masked = `(${digits}`;
    }
    control.setValue(masked, { emitEvent: false });
  }

  enviar(): void {
    this.submitted = true;
    this.enviadoMsg = null;
    this.erroMsg = null;
    this.aplicarValidacaoRepresentante(!!this.form.value.tem_representante);

    if (this.form.invalid) {
      return;
    }
    if (!this.turnstileToken) {
      this.erroMsg = this.transloco.translate('common.notRobot');
      return;
    }

    this.enviando = true;
    const v = this.form.value;
    this.lgpd
      .enviarSolicitacao({
        nome: String(v.nome).trim(),
        cpf: String(v.cpf).trim(),
        email: String(v.email).trim(),
        telefone: v.telefone ? String(v.telefone).trim() : null,
        direito: String(v.direito),
        identificador: v.identificador ? String(v.identificador).trim() : null,
        descricao: String(v.descricao).trim(),
        tem_representante: !!v.tem_representante,
        representante_nome: v.tem_representante ? String(v.representante_nome).trim() : null,
        representante_cpf: v.tem_representante ? String(v.representante_cpf).trim() : null,
        representante_vinculo: v.tem_representante ? String(v.representante_vinculo).trim() : null,
        declaracao_titular: true,
        ciencia_art16: true,
        turnstile_token: this.turnstileToken,
      })
      .subscribe({
        next: (res) => {
          this.enviando = false;
          this.enviadoMsg = res.message;
          this.form.reset({
            direito: this.direitos[0].value,
            tem_representante: false,
            declaracao_titular: false,
            ciencia_art16: false,
          });
          this.submitted = false;
          this.turnstileToken = '';
          this.turnstile?.reset();
        },
        error: (err) => {
          this.enviando = false;
          this.erroMsg = err?.error?.message ?? this.transloco.translate('lgpd.sendError');
          this.turnstile?.reset();
          this.turnstileToken = '';
        },
      });
  }

  private aplicarValidacaoRepresentante(on: boolean): void {
    const nome = this.form.get('representante_nome');
    const cpf = this.form.get('representante_cpf');
    const vinculo = this.form.get('representante_vinculo');
    if (on) {
      nome?.setValidators([Validators.required, Validators.maxLength(120)]);
      cpf?.setValidators([Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)]);
      vinculo?.setValidators([Validators.required, Validators.maxLength(120)]);
    } else {
      nome?.clearValidators();
      cpf?.clearValidators();
      vinculo?.clearValidators();
      nome?.setValue('');
      cpf?.setValue('');
      vinculo?.setValue('');
    }
    nome?.updateValueAndValidity({ emitEvent: false });
    cpf?.updateValueAndValidity({ emitEvent: false });
    vinculo?.updateValueAndValidity({ emitEvent: false });
  }
}
